// Files live on Shopify. Only the columns near the viewport get image elements.
class UndefinedWall extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    const items = this.querySelector('template').content.querySelectorAll('[data-src]');
    this.photos = Array.from(items, item => ({
      url: item.dataset.src,
      ratio: Math.max(0.48, Math.min(2.4, Number(item.dataset.ratio) || 1))
    }));
    this.view = this.querySelector('.ud-view');
    if (!this.photos.length) {
      this.view.removeAttribute('tabindex');
      return;
    }
    this.dataset.ready = '';
    this.events = new AbortController();
    const listen = (target, type, fn, extra = {}) =>
      target.addEventListener(type, fn, { ...extra, signal: this.events.signal });
    this.track = this.querySelector('.ud-track');
    this.motion = this.querySelector('.ud-motion');
    this.activeColumns = new Map();
    this.gap = Number(this.dataset.gap);
    this.speed = Number(this.dataset.speed);
    this.auto = this.dataset.auto === 'true';
    this.paused = false;
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.holdUntil = 0;
    this.fraction = 0;
    this.focused = false;
    this.resize = new ResizeObserver(() => this.layout());
    this.resize.observe(this.view);
    listen(this.view, 'scroll', () => this.render(), { passive: true });
    listen(this.view, 'wheel', e => {
      if (e.ctrlKey || !e.cancelable) return;
      e.preventDefault();
      this.hold();
      const axis = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? this.view.clientWidth : 1;
      this.view.scrollLeft += axis * unit;
    }, { passive: false });
    listen(this.view, 'keydown', e => {
      if (!['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      e.preventDefault();
      this.hold();
      this.view.scrollLeft += this.step * (e.key === 'ArrowLeft' ? -1 : 1);
    });
    listen(this.view, 'pointerdown', e => {
      if (!e.isPrimary || e.button !== 0) return;
      this.hold();
      this.pointer = e.pointerId;
      if (e.pointerType !== 'mouse') return;
      this.dragX = e.clientX;
      this.view.setPointerCapture(e.pointerId);
    });
    listen(this.view, 'pointermove', e => {
      if (this.dragX == null || this.pointer !== e.pointerId) return;
      this.view.scrollLeft += this.dragX - e.clientX;
      this.dragX = e.clientX;
    });
    const release = e => {
      if (e.pointerId !== this.pointer) return;
      this.pointer = null;
      this.dragX = null;
      this.hold();
    };
    listen(window, 'pointerup', release);
    listen(window, 'pointercancel', release);
    listen(this.view, 'lostpointercapture', release);
    const card = this.parentElement.querySelector('.ud-card');
    listen(card, 'focusin', () => { this.focused = true; this.updateMotion(); });
    listen(card, 'focusout', e => {
      this.focused = card.contains(e.relatedTarget);
      this.updateMotion();
    });
    listen(this.motion, 'click', () => { this.paused = !this.paused; this.updateMotion(); });
    listen(this.reduced, 'change', () => this.updateMotion());
    listen(document, 'visibilitychange', () => this.updateMotion());
    this.layout();
    this.updateMotion();
  }

  disconnectedCallback() {
    this.events?.abort();
    this.events = null;
    this.resize?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.pointer = this.dragX = null;
    this.track?.replaceChildren();
    this.sizeKey = '';
  }

  hold() {
    this.holdUntil = performance.now() + 1600;
  }

  layout() {
    const w = this.view.clientWidth, h = this.view.clientHeight;
    if (!w || !h || this.sizeKey === w + '/' + h) return;
    this.sizeKey = w + '/' + h;
    const columnWidth = Math.round(w < 680 ? Math.max(112, w / 2.45) : Math.min(280, Math.max(150, w / 7.7)));
    this.step = columnWidth + this.gap;
    this.columns = [];
    let index = 0;
    // Complete the final column with photos from the beginning so there is no empty seam.
    while (index < this.photos.length) {
      const tiles = [];
      let total = 0;
      while (total < h) {
        const photo = this.photos[index % this.photos.length];
        const height = Math.min(h, columnWidth / photo.ratio);
        tiles.push({ photo, height });
        total += height + this.gap;
        index++;
      }
      const usable = h - (tiles.length - 1) * this.gap;
      const natural = total - tiles.length * this.gap;
      this.columns.push(tiles.map(tile => ({ photo: tile.photo, height: tile.height * usable / natural })));
    }
    // Whole repeated cycles leave room to wrap even when the merchant has only one photo.
    const cycle = this.columns.length * this.step;
    this.band = cycle * Math.ceil((2 * w + this.step) / cycle);
    this.track.style.width = (3 * this.band) + 'px';
    this.track.replaceChildren();
    this.activeColumns.clear();
    this.view.scrollLeft = this.band;
    this.render();
  }

  render() {
    if (!this.band) return;
    let x = this.view.scrollLeft;
    // Recenter by a whole band. The photo at every viewport coordinate stays identical.
    if (x < this.band / 2 || x >= this.band * 2) {
      x = this.band + ((x % this.band) + this.band) % this.band;
      this.view.scrollLeft = x;
    }
    const first = Math.max(0, Math.floor(x / this.step) - 1);
    const last = Math.ceil((x + this.view.clientWidth) / this.step);
    for (const [i, column] of this.activeColumns) {
      if (i < first || i > last) { column.remove(); this.activeColumns.delete(i); }
    }
    for (let i = first; i <= last; i++) {
      if (this.activeColumns.has(i)) continue;
      const column = document.createElement('div');
      column.className = 'ud-column';
      column.style.left = (i * this.step) + 'px';
      column.style.width = (this.step - this.gap) + 'px';
      column.style.gap = this.gap + 'px';
      for (const tile of this.columns[i % this.columns.length]) {
        const img = document.createElement('img');
        img.alt = '';
        img.draggable = false;
        img.decoding = 'async';
        img.style.height = tile.height + 'px';
        img.src = tile.photo.url;
        column.append(img);
      }
      this.track.append(column);
      this.activeColumns.set(i, column);
    }
  }

  updateMotion() {
    cancelAnimationFrame(this.frame);
    const allowed = this.auto && !this.reduced.matches;
    this.motion.hidden = !allowed;
    this.motion.textContent = this.paused ? 'Resume motion' : 'Pause motion';
    if (!allowed || this.paused || document.hidden || this.focused) return;
    let last = performance.now();
    const tick = now => {
      if (now >= this.holdUntil && this.pointer == null) {
        this.fraction += Math.min(now - last, 64) * this.speed / 1000;
        const pixels = Math.floor(this.fraction);
        if (pixels) {
          this.fraction -= pixels;
          this.view.scrollLeft += pixels;
        }
      }
      last = now;
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }
}

if (!customElements.get('undefined-wall')) customElements.define('undefined-wall', UndefinedWall);
